import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { pangoApi, LoginRequest, LoginResponse } from '../services/pangoApi';
import { generateOutletAvatar, getAvatarStyleForOutletType } from '../lib/avatar';

// Types
interface User {
  outlet_id: string;
  name: string;
  outlet_type: string;
  region: string;
  contact_person: string;
  phone: string;
  city_tier: string;
  store_size: string;
  customer_volume: string;
  operating_hours: string;
  customer_type: string;
  avatar?: string;
  scoring: {
    erfm_score: number;
    mission_score: number;
    order_score: number;
    performance_score: number;
    overall_score: number;
    score_tier: string;
  };
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<{ success: boolean; message?: string }>;
  logout: () => Promise<void>;
  error: string | null;
}

// Create Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Provider Component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Start with loading true
  const [error, setError] = useState<string | null>(null);

  // Check for existing session on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('pango_token');
    const savedUser = localStorage.getItem('pango_user');

    if (savedToken && savedUser) {
      try {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Error parsing saved user data:', error);
        localStorage.removeItem('pango_token');
        localStorage.removeItem('pango_user');
      }
    }
    
    // Set loading to false after checking localStorage
    setIsLoading(false);
  }, []);


  // Login function
  const login = async (credentials: LoginRequest): Promise<{ success: boolean; message?: string }> => {
    setIsLoading(true);
    setError(null);

    try {
      // Check API configuration
      const apiBaseUrl = process.env.REACT_APP_PANGO_API_BASE_URL;
      const apiKey = process.env.REACT_APP_PANGO_API_KEY;
      
      if (!apiBaseUrl || !apiKey) {
        throw new Error('Cấu hình API không đầy đủ. Vui lòng kiểm tra biến môi trường.');
      }

      const response: LoginResponse = await pangoApi.login(credentials);

      if (response.success && response.data.authentication.token && response.data.outlet) {
        // Generate avatar for outlet
        const outlet = response.data.outlet;
        const avatarStyle = getAvatarStyleForOutletType(outlet.outlet_type);
        const avatarUrl = generateOutletAvatar(outlet.outlet_id, avatarStyle);
        
        const userWithAvatar = {
          ...outlet,
          avatar: avatarUrl
        };

        // Save to state immediately
        setToken(response.data.authentication.token);
        setUser(userWithAvatar);

        // Save to localStorage
        localStorage.setItem('pango_token', response.data.authentication.token);
        localStorage.setItem('pango_user', JSON.stringify(userWithAvatar));



        // Force page reload to ensure navigation
        setTimeout(() => {
          window.location.href = '/';
        }, 100);

        return { success: true };
      } else {
        const errorMessage = response.message || 'Đăng nhập thất bại';
        setError(errorMessage);
        return { success: false, message: errorMessage };
      }
    } catch (error) {
      
      let errorMessage = 'Có lỗi xảy ra khi đăng nhập';
      
      if (error instanceof Error) {
        // Handle specific error messages
        if (error.message.includes('fetch')) {
          errorMessage = 'Không thể kết nối đến máy chủ. Kiểm tra kết nối mạng.';
        } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          errorMessage = 'Thông tin đăng nhập không chính xác.';
        } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
          errorMessage = 'Bạn không có quyền truy cập.';
        } else if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
          errorMessage = 'Lỗi server. Vui lòng thử lại sau.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      if (token && user) {
        // Call logout API
        await pangoApi.logout(token, user.outlet_id);
      }
    } catch (error) {
      // Continue with local logout even if API fails
      // This ensures user can still logout even if server is down
    } finally {
      // Always clear local state
      setUser(null);
      setToken(null);
      setError(null);
      localStorage.removeItem('pango_token');
      localStorage.removeItem('pango_user');
    }
  };

  // Check if user is authenticated
  const isAuthenticated = !!user && !!token;

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated,
    isLoading,
    login,
    logout,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
